/**
 * Task #1681 — Single source of truth dei "canali" notifica (WhatsApp, SMS,
 * Telegram, Email, Chat interna, Feed, Telefonia, Sistema). Importato sia dal
 * backend (titolo push, dispatching) sia dal frontend (NotificationBell,
 * NotificationsSettingsTab, mini-preview, badge/icon).
 *
 * Le categorie di preferenza (NotificationCategory) restano il granulare per
 * il gating utente — questo file mappa SOLO il "veicolo visivo" comune che
 * accomuna piu' categorie (es. sms_inbound, whatsapp_inbound, telegram_inbound
 * appartengono tutte alla famiglia "messaging" ma con brand distinto).
 */

export type NotificationChannelKey =
  | 'whatsapp'
  | 'sms'
  | 'telegram'
  | 'instagram'
  | 'messenger'
  | 'email'
  | 'chat'
  | 'feed'
  | 'voice'
  | 'system'
  | 'calendar'
  | 'video';

export interface NotificationChannelDef {
  /** Etichetta utente (IT). */
  label: string;
  /**
   * Hex color principale (usato per badge in chat, mini-preview, tag colorato
   * nella campanella). Mantenere coerente con le linee guida Aurora.
   */
  color: string;
  /**
   * Path PNG assoluto per l'icona principale (usata dal SW come `icon` della
   * push e dalla campanella per il glyph del canale). Tutti gli asset vivono
   * in `packages/frontend/public/notifications/channels/`.
   */
  icon: string;
  /**
   * Path PNG assoluto per il badge piccolo (Android usa `badge`). Fallback al
   * badge globale di EasyFlow se mancante.
   */
  badge: string;
  /**
   * Nome icona Lucide consigliato (consumato solo lato FE per renderizzare
   * `<Icon />`). Non importare lucide-react in questo file shared per evitare
   * di trascinare la dep nel bundle backend.
   */
  lucide: string;
  /**
   * Prefisso del titolo "branded" emesso lato backend prima del body del push
   * (es. "WhatsApp · Mario Rossi"). Lasciare null per i canali che non hanno
   * bisogno di branding aggiuntivo (es. system).
   */
  titlePrefix: string | null;
}

export const NOTIFICATION_CHANNELS: Record<NotificationChannelKey, NotificationChannelDef> = {
  whatsapp: {
    label: 'WhatsApp',
    color: '#25D366',
    icon: '/notifications/channels/whatsapp-192.png',
    badge: '/notifications/channels/whatsapp-badge-96.png',
    lucide: 'MessageCircle',
    titlePrefix: 'WhatsApp',
  },
  sms: {
    label: 'SMS',
    color: '#3B82F6',
    icon: '/notifications/channels/sms-192.png',
    badge: '/notifications/channels/sms-badge-96.png',
    lucide: 'MessageSquare',
    titlePrefix: 'SMS',
  },
  telegram: {
    label: 'Telegram',
    color: '#229ED9',
    icon: '/notifications/channels/telegram-192.png',
    badge: '/notifications/channels/telegram-badge-96.png',
    lucide: 'Send',
    titlePrefix: 'Telegram',
  },
  instagram: {
    label: 'Instagram',
    color: '#E1306C',
    icon: '/notifications/channels/telegram-192.png',
    badge: '/notifications/channels/telegram-badge-96.png',
    lucide: 'Instagram',
    titlePrefix: 'Instagram',
  },
  messenger: {
    label: 'Messenger',
    color: '#0084FF',
    icon: '/notifications/channels/telegram-192.png',
    badge: '/notifications/channels/telegram-badge-96.png',
    lucide: 'MessageCircle',
    titlePrefix: 'Messenger',
  },
  email: {
    label: 'Email',
    color: '#0EA5E9',
    icon: '/notifications/channels/email-192.png',
    badge: '/notifications/channels/email-badge-96.png',
    lucide: 'Mail',
    titlePrefix: 'Email',
  },
  chat: {
    label: 'Chat interna',
    color: '#6366F1',
    icon: '/notifications/channels/chat-192.png',
    badge: '/notifications/channels/chat-badge-96.png',
    lucide: 'MessageSquare',
    titlePrefix: 'Chat',
  },
  feed: {
    label: 'Feed',
    color: '#A855F7',
    icon: '/notifications/channels/feed-192.png',
    badge: '/notifications/channels/feed-badge-96.png',
    lucide: 'Newspaper',
    titlePrefix: 'Feed',
  },
  voice: {
    label: 'Telefonia',
    color: '#22C55E',
    icon: '/notifications/channels/voice-192.png',
    badge: '/notifications/channels/voice-badge-96.png',
    lucide: 'Phone',
    titlePrefix: null,
  },
  system: {
    label: 'Sistema',
    color: '#F59E0B',
    icon: '/notifications/notification-icon-192.png',
    badge: '/notifications/notification-badge-96.png',
    lucide: 'AlertTriangle',
    titlePrefix: null,
  },
  calendar: {
    label: 'Calendario',
    color: '#7C3AED',
    icon: '/notifications/channels/calendar-192.png',
    badge: '/notifications/channels/calendar-badge-96.png',
    lucide: 'CalendarClock',
    titlePrefix: 'Calendario',
  },
  video: {
    label: 'Video Call',
    color: '#EF4444',
    icon: '/notifications/channels/video-192.png',
    badge: '/notifications/channels/video-badge-96.png',
    lucide: 'Video',
    titlePrefix: 'Video Call',
  },
};

/**
 * Mappa categoria → canale visivo. Allineata con NotificationCategory di
 * `packages/backend/src/services/notifications/notification.service.ts`. Estendere QUI
 * quando si aggiunge una nuova categoria.
 */
export const CATEGORY_TO_CHANNEL: Record<string, NotificationChannelKey> = {
  // Telefonia
  incoming_calls: 'voice',
  missed_calls: 'voice',
  voicemail: 'voice',
  appointment_reassigned: 'voice',
  // Messaggistica
  sms_inbound: 'sms',
  whatsapp_inbound: 'whatsapp',
  telegram_inbound: 'telegram',
  messaging: 'chat',
  chat_message: 'chat',
  chat_mention: 'chat',
  // Email
  email_received: 'email',
  // Feed
  feed_post: 'feed',
  feed_comment: 'feed',
  feed_mention: 'feed',
  feed_reaction: 'feed',
  feed_report: 'feed',
  // Sistema / operatività
  system_alert: 'system',
  service_alert: 'system',
  storage_alert: 'system',
  storage_quota: 'system',
  warning: 'system',
  workflow_notification: 'system',
  invoice_lifecycle: 'system',
  hr_request: 'system',
  task_event: 'system',
  crm_event: 'system',
  lead_inbound: 'system',
  support_ticket: 'system',
  workgroup_invite: 'system',
  sip_admin: 'system',
  pos_fiscal: 'system',
  fiscal_z_mismatch: 'system',
  fiscal_receipt_expired: 'system',
  pos_logistics: 'system',
  pos_receipt_discrepancy: 'system',
  pos_inventory_variance: 'system',
  pos_supplier_import_failed: 'system',
  pos_transfer_stale: 'system',
  // Calendario
  calendar_appointment: 'calendar',
  calendar_task: 'calendar',
  // Video Call Live
  calendar_videocall: 'video',
  video_invitation: 'video',
};

export function channelForCategory(category: string | null | undefined): NotificationChannelKey {
  if (!category) return 'system';
  return CATEGORY_TO_CHANNEL[category] || 'system';
}

/**
 * Risolve il canale visivo a partire dal `channel` di messaggio CRM
 * (`messaging_messages.channel` / `messaging_conversations.channel`).
 */
export function channelForMessagingChannel(raw: string | null | undefined): NotificationChannelKey {
  switch ((raw || '').toLowerCase()) {
    case 'sms': return 'sms';
    case 'whatsapp': return 'whatsapp';
    case 'telegram': return 'telegram';
    case 'instagram': return 'instagram';
    case 'messenger': return 'messenger';
    default: return 'chat';
  }
}

/**
 * Risolve il canale visivo di una notifica a partire dal suo `type` server e
 * dal `metadata`. Ritorna `null` quando il tipo non ha un canale visivo
 * dedicato (il consumer ricade sul proprio fallback per-tipo). Centralizza la
 * logica condivisa da campanella in-app (NotificationBell) e popup desktop
 * (NotificationHandler), e funziona anche sulle righe storiche prive di
 * `metadata.channel`.
 */
export function channelForNotification(
  type: string,
  metadata?: { channel?: unknown; event_type?: unknown } | null,
): NotificationChannelKey | null {
  const rawChannel = metadata && typeof metadata.channel === 'string'
    ? metadata.channel.toLowerCase()
    : null;
  // Un tag `channel` esplicito e valido vince (es. messaging sms/whatsapp/telegram).
  if (rawChannel && rawChannel in NOTIFICATION_CHANNELS) {
    return rawChannel as NotificationChannelKey;
  }
  if (type === 'message_received') {
    return channelForMessagingChannel(rawChannel);
  }
  // I reminder calendario condividono il type `calendar_reminder`: il tipo reale
  // (appuntamento / task / videocall) vive in `metadata.event_type`.
  if (type === 'calendar_reminder') {
    const eventType = metadata && typeof metadata.event_type === 'string'
      ? metadata.event_type.toLowerCase()
      : null;
    return eventType === 'videocall' ? 'video' : 'calendar';
  }
  if (type === 'video_invitation') return 'video';
  if (type === 'email_received') return 'email';
  return null;
}
